/**
 * Deal status sets and helpers for order editability and actions.
 * Aligns with backend submitAndCancelAllowedDealStatuses / draftAllowedForReviewSubmitDealStatuses.
 * All sets derived from DealStatusEnum (single source of truth).
 */

import { DealStatusEnum, LEGACY_NOT_PUBLISHED_STATUS } from '../deals.types';

const normalizeDealStatus = (dealStatus: string | null | undefined): string | null => {
  if (typeof dealStatus !== 'string') return null;
  const trimmedStatus = dealStatus.trim();
  if (trimmedStatus.toLowerCase() === LEGACY_NOT_PUBLISHED_STATUS.toLowerCase()) {
    return DealStatusEnum.EXPECTED;
  }
  return trimmedStatus;
};

/** The ONLY 3 statuses that allow submit/cancel. Aligns with backend submitAndCancelAllowedDealStatuses. */
export const DEAL_STATUSES_SUBMIT_CANCEL_ALLOWED: ReadonlySet<string> = new Set([
  DealStatusEnum.PRELIMINARY_PRICING,
  DealStatusEnum.RETAIL_ORDER_PERIOD,
  DealStatusEnum.VERBAL_AWARD_CXL_WINDOW,
]);

export const isDealStatusAllowedForSubmitOrCancel = (
  dealStatus: string | null | undefined,
): boolean => {
  const normalized = normalizeDealStatus(dealStatus);
  return !!normalized && DEAL_STATUSES_SUBMIT_CANCEL_ALLOWED.has(normalized);
};

/** Preliminary Pricing + Retail Order Period — draft review/submit, non-transit edit, order period dates. Aligns with backend draftAllowedForReviewSubmitDealStatuses. */
const DEAL_STATUSES_PP_AND_ROP: ReadonlySet<string> = new Set([
  DealStatusEnum.PRELIMINARY_PRICING,
  DealStatusEnum.RETAIL_ORDER_PERIOD,
]);

export const isDealStatusAllowedForDraftReviewSubmit = (dealStatus: string | null | undefined): boolean => {
  const normalizedStatus = normalizeDealStatus(dealStatus);
  return !!normalizedStatus && DEAL_STATUSES_PP_AND_ROP.has(normalizedStatus);
};

/**
 * True when first-time draft submit is allowed for the deal (Preliminary Pricing / Retail Order Period
 * with order period open). Requires deal in submit/cancel allowed statuses and PP/ROP subset.
 */
export const isDealStatusAllowedForDraftSubmit = (
  dealStatus: string | null | undefined,
  orderPeriodBeginDate: string | null | undefined,
  orderPeriodEndDate: string | null | undefined,
): boolean => {
  if (!isDealStatusAllowedForSubmitOrCancel(dealStatus)) return false;
  if (!isDealStatusAllowedForDraftReviewSubmit(dealStatus)) return false;
  return !isOrderPeriodOutsideWindow(dealStatus, orderPeriodBeginDate, orderPeriodEndDate);
};

/** True when reconfirm submit may be offered (Verbal Award/CXL Window; order-level rules still apply). */
export const isDealStatusAllowedForReconfirmSubmit = (dealStatus: string | null | undefined): boolean =>
  isRepriceWindow(dealStatus);

export const isDealStatusAllNonTransitEditable = (dealStatus: string | null | undefined): boolean =>
{
  const normalizedStatus = normalizeDealStatus(dealStatus);
  return !!normalizedStatus && DEAL_STATUSES_PP_AND_ROP.has(normalizedStatus);
};

/** True when deal status is Verbal Award/CXL Window (reprice/reconfirm window). */
export const isRepriceWindow = (dealStatus: string | null | undefined): boolean =>
  normalizeDealStatus(dealStatus) === DealStatusEnum.VERBAL_AWARD_CXL_WINDOW;

/** True when order period dates and expiry logic should run (matches backend validateOrderPeriodNotEnded). */
export const isOrderPeriodDatesApplicable = (dealStatus: string | null | undefined): boolean =>
{
  const normalizedStatus = normalizeDealStatus(dealStatus);
  return !!normalizedStatus && DEAL_STATUSES_PP_AND_ROP.has(normalizedStatus);
};

/**
 * Parse ORDER_PERIOD_* values from Genesis (epoch ms string, ISO, or "yyyy-MM-dd HH:mm:ss.SSS Z").
 * Do not use Number() on datetime strings — e.g. Number("2025-08-01 …") === 2025.
 */
export const parseOrderPeriodDateMs = (
  value: string | number | null | undefined,
): number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (/^\d+$/.test(trimmed)) {
    const epochMs = Number(trimmed);
    return Number.isFinite(epochMs) && epochMs > 0 ? epochMs : null;
  }
  const parsed = new Date(trimmed).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * True when the order period has ended: both dates are present, deal status is
 * Preliminary Pricing or Retail Order Period, and the end date is in the past.
 * Aligns with backend validateOrderPeriodNotEnded.
 */
export const isOrderPeriodExpired = (
  dealStatus: string | null | undefined,
  orderPeriodBeginDate: string | null | undefined,
  orderPeriodEndDate: string | null | undefined,
): boolean => {
  if (!isOrderPeriodDatesApplicable(dealStatus)) return false;
  if (!orderPeriodBeginDate || !orderPeriodEndDate) return false;
  const endMs = parseOrderPeriodDateMs(orderPeriodEndDate);
  if (endMs == null) return false;
  return endMs < Date.now();
};

/**
 * True when order period has not started yet: both dates are present, deal status is
 * Preliminary Pricing or Retail Order Period, and begin date is in the future.
 */
export const isOrderPeriodNotStarted = (
  dealStatus: string | null | undefined,
  orderPeriodBeginDate: string | null | undefined,
  orderPeriodEndDate: string | null | undefined,
): boolean => {
  if (!isOrderPeriodDatesApplicable(dealStatus)) return false;
  if (!orderPeriodBeginDate || !orderPeriodEndDate) return false;
  const beginMs = parseOrderPeriodDateMs(orderPeriodBeginDate);
  if (beginMs == null) return false;
  return beginMs > Date.now();
};

/**
 * True when submit/cancel must be blocked for Preliminary Pricing / Retail Order Period:
 * either date is missing, the window has not started yet, or it has ended.
 * Draft add/edit is not gated by this (aligns with backend validateOrderPeriodNotEnded on submit/cancel).
 */
export const isOrderPeriodOutsideWindow = (
  dealStatus: string | null | undefined,
  orderPeriodBeginDate: string | null | undefined,
  orderPeriodEndDate: string | null | undefined,
): boolean => {
  if (!isOrderPeriodDatesApplicable(dealStatus)) return false;
  if (!orderPeriodBeginDate || !orderPeriodEndDate) return true;
  const beginMs = parseOrderPeriodDateMs(orderPeriodBeginDate);
  const endMs = parseOrderPeriodDateMs(orderPeriodEndDate);
  if (beginMs == null || endMs == null) return true;
  const now = Date.now();
  return beginMs > now || endMs < now;
};
